/**
 * TrainingType Form Component
 * Form for creating and editing TrainingType data
 */
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Paper,
  Typography,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { TrainingTypeFormData, TrainingTypeStatus } from '../../../types/trainingType';

const validationSchema = yup.object({
  name: yup
    .string()
    .required('Nome do tipo de treinamento é obrigatório')
    .min(3, 'Mínimo de 3 caracteres')
    .max(255, 'Máximo de 255 caracteres'),
  description: yup.string().max(2000, 'Máximo de 2000 caracteres'),
  legalBasis: yup.string().max(255, 'Máximo de 255 caracteres'),
  workload: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(1, 'Carga horária deve ser no mínimo 1'),
  periodicityMonths: yup
    .number()
    .nullable()
    .transform((value, original) => (original === '' ? null : value))
    .min(1, 'Periodicidade deve ser no mínimo 1 mês'),
  esocialCode: yup.string().max(10, 'Máximo de 10 caracteres'),
  certificateText: yup.string().max(5000, 'Máximo de 5000 caracteres'),
  status: yup
    .string()
    .oneOf(Object.values(TrainingTypeStatus), 'Status inválido')
    .required('Status é obrigatório'),
});

interface TrainingTypeFormProps {
  initialData?: Partial<TrainingTypeFormData>;
  onSubmit: (data: TrainingTypeFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const TrainingTypeForm: React.FC<TrainingTypeFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const isEditing = Boolean(initialData && 'name' in initialData && initialData.name);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TrainingTypeFormData>({
    resolver: yupResolver(validationSchema as yup.ObjectSchema<TrainingTypeFormData>),
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      legalBasis: initialData?.legalBasis || '',
      workload: initialData?.workload ?? null,
      periodicityMonths: initialData?.periodicityMonths ?? null,
      esocialCode: initialData?.esocialCode || '',
      certificateText: initialData?.certificateText || '',
      status: initialData?.status || TrainingTypeStatus.ACTIVE,
    },
  });

  const handleFormSubmit = async (data: TrainingTypeFormData) => {
    await onSubmit(data);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {/* Identification Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Identificação
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome do Tipo de Treinamento"
                  fullWidth
                  required
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message || 'Ex: Trabalho em Altura - NR-35'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth required error={Boolean(errors.status)}>
              <InputLabel>Status</InputLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Status" disabled={isSubmitting}>
                    <MenuItem value={TrainingTypeStatus.ACTIVE}>Ativo</MenuItem>
                    <MenuItem value={TrainingTypeStatus.INACTIVE}>Inativo</MenuItem>
                  </Select>
                )}
              />
              {errors.status && <FormHelperText>{errors.status.message}</FormHelperText>}
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Descrição"
                  fullWidth
                  multiline
                  rows={3}
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Regulatory Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Informações Regulatórias
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="legalBasis"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Fundamentação Legal"
                  fullWidth
                  error={Boolean(errors.legalBasis)}
                  helperText={errors.legalBasis?.message || 'Ex: NR-35, CTB, Lei 8.213/91'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="esocialCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código eSocial (Tabela 28)"
                  fullWidth
                  error={Boolean(errors.esocialCode)}
                  helperText={errors.esocialCode?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Duration Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Carga Horária e Periodicidade
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="workload"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                  label="Carga Horária (horas)"
                  fullWidth
                  type="number"
                  error={Boolean(errors.workload)}
                  helperText={errors.workload?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="periodicityMonths"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                  label="Periodicidade Padrão (meses)"
                  fullWidth
                  type="number"
                  error={Boolean(errors.periodicityMonths)}
                  helperText={errors.periodicityMonths?.message || 'Ex: 12 = Anual, 24 = Bienal'}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Certificate Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Certificado
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={12}>
            <Controller
              name="certificateText"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Texto do Certificado"
                  fullWidth
                  multiline
                  rows={5}
                  error={Boolean(errors.certificateText)}
                  helperText={
                    errors.certificateText?.message ||
                    'Variáveis disponíveis: {nome_funcionario}, {cpf}, {nome_treinamento}, {carga_horaria}, {data_realizacao}, {data_validade}, {nome_empresa}, {nome_instrutor}'
                  }
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="button"
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>

        <Button
          type="submit"
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSubmitting}
        >
          {isEditing ? 'Atualizar' : 'Criar'} Tipo de Treinamento
        </Button>
      </Box>
    </Box>
  );
};

export default TrainingTypeForm;
